/**
 * Aparência única de campo de formulário (input, select, textarea): raio, borda, fundo,
 * texto, foco e desabilitado, todos por token — troca sozinha no escuro e por tema.
 *
 * Fica fora de Field.tsx para poder ser usado como string por quem ainda monta o
 * próprio <input> (`const inputClass = FIELD_CLASS`). Tela nova deve usar <Input>/<Select>.
 */

export const CONTROL_BASE =
  'block rounded-control border bg-field text-sm text-fg placeholder:text-fg-subtle transition ' +
  'focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-fg-subtle';

export const controlState = (invalid?: boolean): string =>
  invalid
    ? 'border-danger focus:border-danger focus:ring-danger/30'
    : 'border-line-strong focus:border-primary focus:ring-primary/30';

/**
 * Para elementos que ainda não usam <Input>/<Select>: mesma aparência e altura de 40px
 * (borda 2px + linha do text-sm 20px + padding vertical 9px×2). Vale para input e select;
 * em textarea o padding vira só respiro, a altura é a das linhas.
 */
export const FIELD_BASE_CLASS = `${CONTROL_BASE} ${controlState()} px-3 py-[9px]`;

/** FIELD_BASE_CLASS + largura total. Quando o pai fixa a largura (w-72, flex-1), use a BASE. */
export const FIELD_CLASS = `${FIELD_BASE_CLASS} w-full`;

/**
 * Estado de validação de um campo: `danger` (erro), `warning` (obrigatório e faltando) e `hint`
 * (obrigatório, só um lembrete). Cada um troca fundo e borda por token, no claro e no escuro.
 */
export type FieldTone = 'danger' | 'warning' | 'hint';

const TONE_CLASS: Record<FieldTone, string> = {
  danger: 'border-danger bg-danger-soft focus:border-danger focus:ring-danger/30',
  warning: 'border-warning bg-warning-soft focus:border-warning focus:ring-warning/30',
  hint: 'border-warning-line focus:border-warning focus:ring-warning/30',
};

/**
 * Versão com estado, para campo que ainda monta a própria className e precisa marcar erro:
 * `className={fieldClassName({ invalid: !!erros.titulo })}` ou `fieldClassName({ tone: 'warning' })`.
 */
export const fieldClassName = (
  { invalid = false, full = true, tone }: { invalid?: boolean; full?: boolean; tone?: FieldTone } = {},
): string => {
  const state = tone ? TONE_CLASS[tone] :controlState(invalid);
  return `${CONTROL_BASE} ${state} px-3 py-[9px]${full ? ' w-full' : ''}`;
};
